import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, User, ArrowUpRight, Tag, BookOpen } from "lucide-react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500&display=swap');

  .blog-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .blog-root { font-family: 'DM Sans', sans-serif; }
  .blog-heading { font-family: 'Playfair Display', serif; }

  .card-hover {
    transition: transform 0.35s cubic-bezier(0.22,1,0.36,1), box-shadow 0.35s ease;
  }
  .card-hover:hover {
    transform: translateY(-6px);
    box-shadow: 0 24px 48px -12px rgba(0,0,0,0.14);
  }
  .img-zoom img {
    transition: transform 0.6s cubic-bezier(0.22,1,0.36,1);
  }
  .img-zoom:hover img { transform: scale(1.07); }

  .read-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 18px; background: #1c1917; color: #fff;
    border-radius: 2px; font-size: 12px; font-weight: 500;
    letter-spacing: 0.05em; text-transform: uppercase;
    border: none; cursor: pointer; text-decoration: none;
    transition: background 0.2s, gap 0.2s;
    font-family: 'DM Sans', sans-serif;
  }
  .read-btn:hover { background: #b45309; gap: 10px; }

  .cat-pill {
    padding: 6px 16px; border-radius: 2px;
    font-size: 11px; font-weight: 500;
    letter-spacing: 0.07em; text-transform: uppercase;
    cursor: pointer; border: 1px solid transparent;
    transition: all 0.18s; font-family: 'DM Sans', sans-serif;
  }
  .cat-pill.active  { background: #1c1917; color: #fff; border-color: #1c1917; }
  .cat-pill.inactive { background: transparent; color: #57534e; border-color: #d6d3d1; }
  .cat-pill.inactive:hover { border-color: #b45309; color: #b45309; }

  .spinner {
    width: 36px; height: 36px;
    border: 3px solid #e7e5e4; border-top-color: #b45309;
    border-radius: 50%; animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .view-all-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 14px 36px; border: 1.5px solid #1c1917;
    background: transparent; color: #1c1917;
    font-size: 11px; font-weight: 500;
    letter-spacing: 0.07em; text-transform: uppercase;
    cursor: pointer; border-radius: 2px;
    transition: background 0.2s, color 0.2s;
    font-family: 'DM Sans', sans-serif;
  }
  .view-all-btn:hover { background: #1c1917; color: #fff; }
  .divider-line { width: 48px; height: 2px; background: #b45309; margin: 16px auto 0; }
  .badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 4px 10px; background: rgba(180,83,9,0.9); color: #fff;
    font-size: 10px; font-weight: 600;
    letter-spacing: 0.08em; text-transform: uppercase;
    font-family: 'DM Sans', sans-serif;
  }
`;

// Turns a title into a URL-safe slug, e.g. "Modern Interiors 2026" -> "modern-interiors-2026"
export function slugify(text = "") {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function BlogSection() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch("https://archpoint.onrender.com/api/blogs")
      .then((res) => res.json())
      .then((data) => { setBlogs(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const categories = ["All", ...new Set(blogs.map((b) => b.category))];
  const filteredBlogs =
    selectedCategory === "All" ? blogs : blogs.filter((b) => b.category === selectedCategory);
  const visibleBlogs = showAll ? filteredBlogs : filteredBlogs.slice(0, 3);

  return (
    <div className="blog-root">
      <style>{styles}</style>

      <section style={{ padding: "96px 0", background: "#fafaf9", minHeight: "100vh" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px" }}>

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "56px" }}>
            <p style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "#b45309", marginBottom: "12px" }}>
              Journal &amp; Insights
            </p>
            <h2 className="blog-heading" style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 700, color: "#1c1917", lineHeight: 1.15 }}>
              Latest From the Studio
            </h2>
            <div className="divider-line" />
            <p style={{ fontSize: "16px", color: "#78716c", maxWidth: "500px", margin: "20px auto 0", lineHeight: 1.75, fontWeight: 300 }}>
              Architecture, design thinking, and the future of built environments
            </p>
          </div>

          {/* Category Filter */}
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px", marginBottom: "48px" }}>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => { setSelectedCategory(cat); setShowAll(false); }}
                className={`cat-pill ${selectedCategory === cat ? "active" : "inactive"}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
              <div className="spinner" />
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "80px 0", color: "#a8a29e" }}>
              <BookOpen size={40} style={{ margin: "0 auto 16px", opacity: 0.4 }} />
              <p>No articles found.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "28px" }}>
              {visibleBlogs.map((blog) => {
                const slug = blog.slug || slugify(blog.title);
                const to = `/blogs/${slug}-${blog._id}`;
                return (
                  <article
                    key={blog._id}
                    className="card-hover"
                    style={{ background: "#fff", border: "1px solid #e7e5e4", borderRadius: "4px", overflow: "hidden", display: "flex", flexDirection: "column" }}
                  >
                    <Link to={to} className="img-zoom" style={{ position: "relative", height: "220px", overflow: "hidden", background: "#f5f5f4", flexShrink: 0, display: "block" }}>
                      <img src={blog.imageUrl} alt={blog.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      <div style={{ position: "absolute", top: "14px", left: "14px" }}>
                        <span className="badge"><Tag size={9} /> {blog.category}</span>
                      </div>
                    </Link>

                    <div style={{ padding: "24px 24px 28px", display: "flex", flexDirection: "column", flexGrow: 1 }}>
                      <Link to={to} style={{ textDecoration: "none" }}>
                        <h3 className="blog-heading" style={{
                          fontSize: "18px", fontWeight: 700, color: "#1c1917",
                          marginBottom: "10px", lineHeight: 1.35,
                          display: "-webkit-box", WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical", overflow: "hidden",
                        }}>
                          {blog.title}
                        </h3>
                      </Link>

                      <p style={{
                        fontSize: "13.5px", color: "#78716c", lineHeight: 1.75,
                        marginBottom: "20px", fontWeight: 300, flexGrow: 1,
                        display: "-webkit-box", WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {blog.summary}
                      </p>

                      <div style={{
                        display: "flex", justifyContent: "space-between",
                        fontSize: "12px", color: "#a8a29e",
                        paddingBottom: "16px", borderBottom: "1px solid #f5f5f4", marginBottom: "16px",
                      }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                          <User size={12} style={{ color: "#b45309" }} />{blog.author}
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                          <Calendar size={12} style={{ color: "#b45309" }} />
                          {new Date(blog.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                      </div>

                      <Link className="read-btn" to={to}>
                        Read Article <ArrowUpRight size={13} />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {!loading && filteredBlogs.length > 3 && !showAll && (
            <div style={{ textAlign: "center", marginTop: "56px" }}>
              <button className="view-all-btn" onClick={() => setShowAll(true)}>
                View All Articles <ArrowUpRight size={15} />
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}